import { ApplicationSummaryEnhanced, PaymentStatus } from "models/ApplicationSummaryEnhanced"
import MojoLogo from "url:assets/images/mojo-logo.png"
import MojoSpecialistLogo from "url:assets/images/mojo-specialist-logo.png"

const BTL_SPECIALIST_COST = 499

export enum MojoServiceTypes {
  BTL_SPECIALIST = "BTL_SPECIALIST",
  DEFAULT = "DEFAULT",
}

const CONFIG_PRESETS: Record<
  MojoServiceTypes,
  {
    logo: string
    fee: number
    serviceType: MojoServiceTypes
    recommendation: {
      title: string
      description: string
      accept: {
        text: string
        subText: string | null
      }
    }
  }
> = {
  DEFAULT: {
    logo: MojoLogo,
    fee: 0,
    serviceType: MojoServiceTypes.DEFAULT,
    recommendation: {
      title: "Mojo's broker fee",
      description:
        "We don't charge you a penny, all our costs are covered by the lender, these fees never" +
        " affect our advice. Our reputation and livelihood depend on us always giving our " +
        "customers the best possible advice.",
      accept: {
        text: "If you're happy with your recommendation then click to accept and we'll get the wheels in motion to secure your rate",
        subText: null,
      },
    },
  },
  BTL_SPECIALIST: {
    logo: MojoSpecialistLogo,
    fee: BTL_SPECIALIST_COST,
    serviceType: MojoServiceTypes.BTL_SPECIALIST,
    recommendation: {
      title: "Mojo Specialist's application fee",
      description: `A single £${BTL_SPECIALIST_COST} fee is charged for application. This covers the preparation and packaging of your application, plus our dedicated case management service.`,
      accept: {
        text: "If you're happy with your recommendation, click 'Accept' to proceed",
        subText: `Our team will then begin preparing your application for submission to [Lender]. The £${BTL_SPECIALIST_COST} application fee for our Mojo Specialist service is payable once your application is ready. Your Case Manager will be in touch soon to guide you through the next steps`,
      },
    },
  },
}

export function getConfigFromType(serviceType: MojoServiceTypes, feeRemoved: boolean) {
  if (feeRemoved) {
    return CONFIG_PRESETS[MojoServiceTypes.DEFAULT]
  }

  return CONFIG_PRESETS[serviceType] ?? CONFIG_PRESETS[MojoServiceTypes.DEFAULT]
}

export function getClientConfig(
  serviceType: MojoServiceTypes,
  paymentStatus: PaymentStatus | undefined
) {
  const isFeeRemoved = paymentStatus === PaymentStatus.FeeRemoved

  return getConfigFromType(serviceType, isFeeRemoved)
}

export function shouldShowSpecialistAdviseStatus(
  serviceType: MojoServiceTypes,
  application: ApplicationSummaryEnhanced
) {
  const paymentStatus = application?.service?.status
  return (
    serviceType === MojoServiceTypes.BTL_SPECIALIST && paymentStatus !== PaymentStatus.FeeRemoved
  )
}

export function shouldShowSpecialistPaymentStatus(
  serviceType: MojoServiceTypes,
  application: ApplicationSummaryEnhanced
) {
  const paymentStatus = application?.service?.status
  return (
    serviceType === MojoServiceTypes.BTL_SPECIALIST &&
    paymentStatus === PaymentStatus.PaymentRequired
  )
}

export function shouldPayForRecommendation(application: ApplicationSummaryEnhanced) {
  const paymentStatus = application?.service?.status
  return ![PaymentStatus.FeeRemoved, PaymentStatus.Paid].includes(paymentStatus)
}

export function getConfigFromApplication(application: ApplicationSummaryEnhanced) {
  const configKey = findServiceType(application)
  return { ...CONFIG_PRESETS[configKey] }
}

export function findServiceType(
  application: ApplicationSummaryEnhanced
): keyof typeof CONFIG_PRESETS {
  const { service, occupancy } = application
  const isBTL = occupancy?.toLowerCase().trim().includes("rent it out") ?? false
  if (isBTL && service?.level === "Specialist") {
    return MojoServiceTypes.BTL_SPECIALIST
  }
  return MojoServiceTypes.DEFAULT
}
